import { ISubscriptionsService } from 'sharebase/services/subscriptionsService';
import { ToastService } from 'sharebase/services/toastService';
import { TrackingService } from 'sharebase/services/trackingService';
import { CommonUtilsService } from 'util/services';
import {
    DigestFrequency,
    TimeSpanParameter,
    PhoenixSubscriptionSettings,
} from 'phoenix/domain';

require('./notificationSettings.styles');
const template = require('./notificationSettings.template');

/**
 * @ngdoc directive
 * @name sharebase.myAccount.notificationSettings
 * @module sharebase.myAccount
 * @restrict e
 * @description
 * `pxNotificationSettings` provides configuration UI for managing a user's notification and email digest settings.
 * 
 * @usage
 * <px-notification-settings on-load-failure="vm.onSettingsLoadFailure()" on-save-success="vm.onSaveSettings()" on-save-error="vm.onSaveSettingsError()"></px-notification-settings>
 * 
 * @element
 * @input onLoadFailure Callback that executes if an error was encountered when attempting to retrieve the user's notification settings.
 * @input onSaveSuccess Callback that executes when changes to the user's settings have been saved.
 * @input onSaveError Callback that executes if an error was encountered when attempting to save the user's settings.
 */
export function NotificationSettingsDirective():ng.IDirective {
    return {
        restrict: 'E',
        controller: NotificationSettingsTrackedController,
        controllerAs: 'vm',
        bindToController: true,
        scope: {
            onLoadFailure: '&',
            onSaveSuccess: '&',
			onSaveError: '&',
            emailDigestFeatureEnabled: '<',
            notificationSettingsOn:'<'
        },
        template
    };
}

export abstract class NotificationSettingsController {
    public static readonly $inject = [
        '$q',
        'utils',
        'sbToastService',
		'sbSubscriptionsService',
        'gettextCatalog'
    ];

    // Expression bindings defined in the directive
    public onLoadFailure: Function;
    public onSaveSuccess: Function;
    public onSaveError: Function;

    // The raw API preferences object.
    public settings: PhoenixSubscriptionSettings;

    // Changes will be automatically saved as user's make them. To prevent sending a lot of requests,
    // debounce sbSubscriptionsService.saveDigestSettings so we can limit the number of API requests.
    private readonly debouncedSavePreferences: (settings: PhoenixSubscriptionSettings) => ng.IPromise<any>;
    // Promise that references an on-going save process when calling `debouncedSaveSettings`. This is used to
    // determine whether callbacks should be registered on the promise returned from the method, because we
    // only want the callbacks registered and called once per save process.
    private savePreferencePromise = null;

    private _frequency: DigestFrequency;
    public digestFrequency = DigestFrequency;
    public digestEnabled: boolean;

    private digestDayOfWeek: number;
    public digestTimeOfDay : TimeSpanParameter;
    public digestDaySelection = [];
    public digestTimeSelection = [];

    public set dayOfWeek(val: string) {
        this.digestDayOfWeek = this.digestDaySelection.indexOf(val);
    }

    public get dayOfWeek() {
        let value = this.digestDayOfWeek != null? this.digestDayOfWeek : 0;
        return  this.digestDaySelection[value];
    }

    public get frequency(): DigestFrequency {
        return this._frequency;
    }

    public set frequency(val: DigestFrequency) {
        this._frequency = val;
        switch(val){
           case DigestFrequency.Weekly:
                this.dayOfWeek = this.digestDaySelection[0];
           case DigestFrequency.Daily:
                this.digestTimeOfDay= {Hours: 0, Minutes: 0, Seconds: 0};         
           break;
           default:
           { 
                this.digestDayOfWeek = null;
                this.digestTimeOfDay = null;
           }
        }
        this.saveDigestFrequency();
    }

    constructor(
        $q: ng.IQService,
        util: CommonUtilsService,
        private readonly sbToastService:ToastService,
		private readonly sbSubscriptionsService: ISubscriptionsService,
        private readonly gettextCatalog
    ) {
        // Make sure labels are picked up by translation extraction
        const notificationSettings = {
            'Downloads': gettextCatalog.getString('Downloads'),
            'Uploads': gettextCatalog.getString('Uploads'),
            'Edits': gettextCatalog.getString('Edits'),
            'Comments': gettextCatalog.getString('Comments'),
            'Previews': gettextCatalog.getString('Previews'),
            'Deletes': gettextCatalog.getString('Deletes'),
            'Shares': gettextCatalog.getString('Shares')
        };
        this.digestDaySelection = this.getDays(gettextCatalog);
        this.digestTimeSelection = this.getHours();

        this.sbSubscriptionsService.getNotificationSettings()
            .then(settings => {
                this._frequency = settings.NotificationFrequency;
                this.digestEnabled = settings.EnableDigest;
                this.digestDayOfWeek = settings.DigestDayOfWeek;
                this.digestTimeOfDay = settings.DigestTimeOfDay;

                this.settings = Object.keys(settings)
                    .filter(k => (k in notificationSettings))
                    .reduce((all, key) => {
                        let s = settings[key];
                        s.label = gettextCatalog.getString(key);
                        s.ariaOwnedLabel = gettextCatalog.getString('Notify me of {{action}} on items that I have shared', { action: s.label });

                        all[key] = s;
                        
                        return all;
                    }, { }) as PhoenixSubscriptionSettings;
			})
			.catch(error => this.onLoadFailure(error));
        
        this.debouncedSavePreferences = util.debounce(this.sbSubscriptionsService.saveNotificationSettings, 700, this.sbSubscriptionsService);
    }

    public getDays(gettextCatalog : any) {
        return [
            gettextCatalog.getString('Sunday'),
            gettextCatalog.getString('Monday'),
            gettextCatalog.getString('Tuesday'),
            gettextCatalog.getString('Wednesday'),
            gettextCatalog.getString('Thursday'),
            gettextCatalog.getString('Friday'),
            gettextCatalog.getString('Saturday')
        ];
    }
    public getHours() {
        let hours = [];
        for(var i = 0; i < 24; ++i) {
           hours[i] = i;
        }
        return hours;
    }

    public getLocalizedTime(hour: number) {
        let d = new Date();
        d.setHours(hour);
        d.setMinutes(0);
        d.setSeconds(0);
        return d.toLocaleTimeString(navigator.language, {hour: '2-digit', minute:'2-digit'});
    }

    public onSettingChange(action: string, value: boolean) {
		this.saveSettings();
    }

    public onToggleDigests(val: boolean){
		this.saveDigestEnabled();
	};

    public saveDigestFrequency(): void {
		this.saveSettings();
    }

    public saveDigestEnabled() {
        this.saveSettings();
    }

    protected saveSettings(): void {
        let resolveToast = this.sbToastService.showProgressMessage({
            message: this.gettextCatalog.getString('Updating your subscription settings.')
        });
        let data = angular.merge({ }, this.settings, {
            NotificationFrequency: this.frequency,
            EnableDigest: this.digestEnabled,
            DigestDayOfWeek: this.digestDayOfWeek,
            DigestTimeOfDay: this.digestTimeOfDay
        });
        let p = this.debouncedSavePreferences(data);

        if (this.savePreferencePromise === null) {
            p.then(() => this.onSaveSuccess && this.onSaveSuccess())
			 .catch(error => this.onSaveError(error))
             .then(() => {
                this.savePreferencePromise = null;
             })
             .finally(() => resolveToast());
            
			this.savePreferencePromise = p; 
        }
    }
}

export class NotificationSettingsTrackedController extends NotificationSettingsController {
    public static readonly $inject = [
        ...NotificationSettingsController.$inject,
        'sbTrackingService'
    ];

    constructor(
        private readonly $q: ng.IQService,
		util: CommonUtilsService,
		sbToastService:ToastService,
        subscriptionService: ISubscriptionsService,
        gettextCatalog,
        private readonly trackingService: TrackingService
    ) {
        super($q, util, sbToastService, subscriptionService, gettextCatalog);
    }

    public onSettingChange(action: string, value: boolean) {
        let text = `${value ? 'Enable' : 'Disable'} ${action}`;
        this.trackingService.trackClick('Default Notification Updated', text);

        super.onSettingChange(action, value);
    }

    public onToggleDigests(val: boolean) {
        let text = val ? 'Enable' : 'Disable';
        this.trackingService.trackClick('Email Digest Toggled', text);

        super.onToggleDigests(val);
    }

    public saveDigestFrequency(): void {
        let text = `frequency of ${DigestFrequency[this.frequency]}`;
        this.trackingService.trackClick('Email Digest Frequency Updated', text);

        super.saveDigestFrequency();
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/components/myAccount/notificationSettings/notificationSettings.ts